import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { AssetBundle } from "./assets";
import type { ResourceNode } from "@tribez/shared";
import { hashId } from "@tribez/shared";
import { toToon, bakeBaseAO } from "./toon";

// Renders the 6 resource node kinds from server state (state.resources). Per-match
// conversion + HP rolls are server-authoritative; client just instances meshes at the
// positions the server tells it. Placeholder visuals: ElderTree = larger darker MapleTree
// mesh, GoldDeposit = gold-tinted StoneDeposit mesh, CulinaryHerbBush = mint-tinted
// BerryBush mesh. Replace with bespoke art in Phase 2.

export type ResourceKind =
  | "MapleTree" | "ElderTree" | "StoneDeposit"
  | "GoldDeposit" | "BerryBush" | "CulinaryHerbBush"
  // Creature drops (Plan 1): rendered as small re-tinted nature props.
  | "RawStagMeat" | "RawLamb" | "RawFox" | "Leather" | "Wool" | "Antlers"
  // Plan 2 / wider drop kinds: bear / wolf / undead / forageables.
  // Same placeholder strategy — re-tinted nature props until bespoke art.
  | "BearPelt" | "RawBearMeat" | "WhiteBearFang" | "RawWolfMeat"
  | "SkeletalFinger" | "Sageberry" | "SpicyHerbs" | "Nightcrawler"
  | "AncientArtifactFragment"
  // Stone-deposit rare side-drop (D5) + gold-deposit rare side-drop (D5).
  | "Shadowstone" | "RadiantGem"
  // Plant Tree ability spawns saplings that grow toward maturity at 1Hz.
  | "Sapling";

type Tint = {
  color: number;
  emissive?: number;
  emissiveIntensity?: number;
};

// `sources` lets a kind use multiple GLTF variants — each instance picks one
// deterministically by hashing its node id. Single-variant kinds (drops) keep
// the original `source` shape.
type KindDef = {
  source?: (a: AssetBundle) => GLTF;
  sources?: ((a: AssetBundle) => GLTF)[];
  baseScale: number;
  tint?: Tint;
  // Luminance-preserving RECOLOR (hex). Unlike `tint` (which multiplies
  // material.color and so can only darken — useless for shifting the warm-taupe
  // EXPLORER rock toward cool grey), this drives toToon's tintColor hook, which
  // replaces hue while keeping the source's brightness pattern. Used to make
  // harvestable StoneDeposits read as cool GREY stone, distinct from the
  // warm-brown decorative rock doodads, even under the warm Primal grade.
  recolor?: number;
  maxCount: number;
};

// ElderTree variants tower over MapleTree variants thanks to its 2.3 scale (vs Maple
// 0.88), keeping the boss-tree silhouette distinct even with shared CommonTree shapes.
// Phase 2 swap: ElderTree now uses Pine_1..5 (5 conifer variants) instead of single
// pine3, MapleTree uses CommonTree_1..5 instead of single commonTree5.
// GoldDeposit needs strong emissive: Quaternius vertex colors multiply against material
// color, washing tints out. Emissive ignores vertex color and self-glows.
const KINDS: Record<ResourceKind, KindDef> = {
  // Harvestable maples share the commonTree meshes with the ~1,015 decorative
  // HavenTree doodads (doodads.ts), so they were indistinguishable from un-choppable
  // scenery. A warm-green tint + faint self-glow makes the 287 choppable trees read
  // as lush/alive against the flatter decorative forest (emissive carries the "pop"
  // since Quaternius vertex colors multiply plain tints toward washout — see note above).
  // Stone-age re-theme: harvestable maples are the GREEN EXPLORER trees (Tree_1x).
  // "Green = harvestable" is the player-readable rule, reinforced by the warm-green
  // tint + faint self-glow against the warm/autumn decorative forest. Native heights:
  // 1A/1B ≈8.9m, 1E ≈5m → scale 0.7 gives ~4–6m maples with natural size variety.
  MapleTree:        { sources: [(a) => a.nature.exTree1A, (a) => a.nature.exTree1B, (a) => a.nature.exTree1E], baseScale: 0.70, tint: { color: 0xffffff, emissive: 0x1c2c0c, emissiveIntensity: 0.14 }, maxCount: 287 },
  // ElderTree = the giant golden EXPLORER Tree_8D (≈21.6m native → scale 0.6 ≈13m,
  // ~2× a maple = unmistakable boss-tree silhouette). It carries the SAME green
  // harvestable tint as the maple so the "green = harvestable" rule holds even
  // though the source mesh is golden.
  ElderTree:        { sources: [(a) => a.nature.exTree8D], baseScale: 0.60, tint: { color: 0xffffff, emissive: 0x4a3814, emissiveIntensity: 0.22 }, maxCount: 20 },
  // Cool-grey recolor so harvestable stone reads as GREY stone, not the warm-taupe
  // EXPLORER rock atlas (which warms further under the Primal grade). Distinguishes
  // harvestable deposits from the brown decorative rock doodads. Cool-biased hex so
  // it lands neutral-grey after the warm key light + sepia grade.
  StoneDeposit:     { sources: [(a) => a.nature.exRockM1A, (a) => a.nature.exRockM2A, (a) => a.nature.exRockM3A], baseScale: 0.80, recolor: 0xc4ccda, maxCount: 233 },
  GoldDeposit:      { sources: [(a) => a.nature.exRockM1A, (a) => a.nature.exRockM2A, (a) => a.nature.exRockM3A], baseScale: 0.80, tint: { color: 0xffd24d, emissive: 0x806010, emissiveIntensity: 0.6 }, maxCount: 20 },
  BerryBush:        { sources: [(a) => a.nature.bushCommon, (a) => a.nature.bushCommonFlowers], baseScale: 0.63, maxCount: 99 },
  CulinaryHerbBush: { sources: [(a) => a.nature.bushCommon, (a) => a.nature.bushCommonFlowers], baseScale: 0.63, tint: { color: 0xb8e87a, emissive: 0x223b14, emissiveIntensity: 0.25 }, maxCount: 24 },
  // Creature drops: small (~0.4u) tinted props that act as ground pickups.
  // Pickup hp=1 in server (drops.ts), so a single right-click swing collects them.
  // maxCount sized for the worst case: each kobold killing every creature within
  // a quota burst → 200+ drops in flight. Round up to 256 per pool to be safe.
  // Drops stay single-variant — re-tinted nature props read fine without variety.
  RawStagMeat:      { source: (a) => a.nature.mushroomCommon, baseScale: 0.45, tint: { color: 0xa53b2e, emissive: 0x2a0808, emissiveIntensity: 0.2 }, maxCount: 256 },
  RawLamb:          { source: (a) => a.nature.mushroomCommon, baseScale: 0.40, tint: { color: 0xe6a98a, emissive: 0x3a1a14, emissiveIntensity: 0.2 }, maxCount: 256 },
  RawFox:           { source: (a) => a.nature.mushroomCommon, baseScale: 0.35, tint: { color: 0xc56a3b, emissive: 0x301505, emissiveIntensity: 0.2 }, maxCount: 256 },
  Leather:          { source: (a) => a.nature.pebbleRound3,   baseScale: 0.30, tint: { color: 0x6b3d24 },                                                maxCount: 256 },
  Wool:             { source: (a) => a.nature.bushCommon,     baseScale: 0.30, tint: { color: 0xe8e6e0 },                                                maxCount: 256 },
  Antlers:          { source: (a) => a.nature.commonTree5,    baseScale: 0.20, tint: { color: 0xd6c8a0 },                                                maxCount: 128 },
  // Bear-class drops
  BearPelt:         { source: (a) => a.nature.pebbleRound3,   baseScale: 0.65, tint: { color: 0x4a2818 },                                                maxCount: 128 },
  RawBearMeat:      { source: (a) => a.nature.mushroomCommon, baseScale: 0.50, tint: { color: 0x802020, emissive: 0x200808, emissiveIntensity: 0.2 },    maxCount: 128 },
  WhiteBearFang:    { source: (a) => a.nature.pebbleRound3,   baseScale: 0.25, tint: { color: 0xf0e8d0, emissive: 0x303028, emissiveIntensity: 0.15 },   maxCount: 32  },
  // Wolf / undead drops
  RawWolfMeat:      { source: (a) => a.nature.mushroomCommon, baseScale: 0.42, tint: { color: 0xa01818, emissive: 0x300808, emissiveIntensity: 0.2 },    maxCount: 128 },
  SkeletalFinger:   { source: (a) => a.nature.pebbleRound3,   baseScale: 0.30, tint: { color: 0xe8d8b8, emissive: 0x302820, emissiveIntensity: 0.1 },    maxCount: 64  },
  // Forageable drops
  Sageberry:        { source: (a) => a.nature.bushCommon,     baseScale: 0.28, tint: { color: 0x8040a0, emissive: 0x201030, emissiveIntensity: 0.25 },   maxCount: 64  },
  SpicyHerbs:       { source: (a) => a.nature.bushCommon,     baseScale: 0.28, tint: { color: 0xd05030, emissive: 0x301810, emissiveIntensity: 0.25 },   maxCount: 64  },
  Nightcrawler:     { source: (a) => a.nature.pebbleRound3,   baseScale: 0.22, tint: { color: 0x402030 },                                                maxCount: 64  },
  AncientArtifactFragment: { source: (a) => a.nature.rockMedium3, baseScale: 0.40, tint: { color: 0xa07030, emissive: 0x302010, emissiveIntensity: 0.4 }, maxCount: 32 },
  // Rare deposit side-drops (Shadowstone on stone, RadiantGem on gold).
  // Visually distinct from the parent deposit so the player notices the rare drop.
  Shadowstone:      { source: (a) => a.nature.rockMedium3,    baseScale: 0.40, tint: { color: 0x4a4060, emissive: 0x201038, emissiveIntensity: 0.5 },    maxCount: 64  },
  RadiantGem:       { source: (a) => a.nature.rockMedium3,    baseScale: 0.40, tint: { color: 0xffe080, emissive: 0xc0a030, emissiveIntensity: 0.8 },    maxCount: 32  },
  // Plant Tree saplings — small bright-green seedling. Reuses the maple variant 1
  // mesh at 0.28 scale for a clearly-juvenile silhouette; emissive tint makes the
  // fresh growth read at a glance against the mature trees nearby. Sapling.hp
  // ticks 0→100 over ~70s server-side; the placeholder mesh stays a constant size
  // for v1 (a hp-driven scale ramp is a future polish pass).
  Sapling:          { source: (a) => a.nature.commonTree1,    baseScale: 0.28, tint: { color: 0x88ff88, emissive: 0x205020, emissiveIntensity: 0.4 },    maxCount: 64  },
};

// Findability pass (2026-06-17): creature/forage drops were 0.2–0.65u with weak or
// no self-glow, so they vanished against the ground at MOBA zoom. Inflate every drop
// kind's silhouette and floor its emissive so loot reads at a glance. Resource nodes
// + saplings are excluded (DROP_KINDS only).
const DROP_KINDS: ResourceKind[] = [
  "RawStagMeat", "RawLamb", "RawFox", "Leather", "Wool", "Antlers",
  "BearPelt", "RawBearMeat", "WhiteBearFang", "RawWolfMeat", "SkeletalFinger",
  "Sageberry", "SpicyHerbs", "Nightcrawler", "AncientArtifactFragment",
  "Shadowstone", "RadiantGem",
];
const DROP_SCALE_MULT = 3.0;
const DROP_GLOW_FLOOR = 0.8;
for (const k of DROP_KINDS) {
  const d = KINDS[k];
  d.baseScale = Math.round(d.baseScale * DROP_SCALE_MULT * 1000) / 1000;
  const t = d.tint!; // every drop kind defines at least a color tint
  // Matte drops (leather/wool/antlers/nightcrawler) lack an emissive — derive a
  // gentle in-hue glow from their base color so they catch the eye too.
  if (t.emissive === undefined) t.emissive = t.color;
  t.emissiveIntensity = Math.max(t.emissiveIntensity ?? 0, DROP_GLOW_FLOOR);
}
// Fast set for drop-kind checks at add/remove time.
const DROP_KINDS_SET = new Set<ResourceKind>(DROP_KINDS);

const ZERO_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

// ── HARVESTABLE RESOURCE NODE OUTLINES (Task A) ───────────────────────────
// Every resource node a player can harvest gets a light inverted-hull toon
// outline via a companion InstancedMesh rendered BackSide with normals
// inflated. Distinguishes them from the ~1,015 decorative doodads that share
// the same GLTF meshes. Saplings (Plant Tree ability) included — they're
// interactable resources too.
const HARVESTABLE_NODE_KINDS = new Set<ResourceKind>([
  "MapleTree", "ElderTree", "StoneDeposit", "GoldDeposit",
  "BerryBush", "CulinaryHerbBush", "Sapling",
]);
// Local-space push (world size ≈ uOW × baseScale). 0.08 gives ~5–7 cm at
// tree / rock / bush scale — visible from MOBA zoom without reading heavy.
const NODE_OUTLINE_WIDTH = 0.08;

// ── CREATURE DROP OUTLINES (Task B, secondary visual) ────────────────────
// Drop InstancedMesh props (tiny pebbles / mushrooms at 1–2 u scale) get a
// narrower outline so the black ring is proportional to the small silhouette.
const DROP_OUTLINE_WIDTH = 0.04;

// Inverted-hull outline for InstancedMesh resource / drop pools.
//
// Uses MeshLambertMaterial as the base because MeshBasicMaterial does NOT run
// the normal pipeline, so `objectNormal` would be undefined. With a pure black
// colour the lit computation is 0 × anything = 0, so lights don't matter.
//
// The push happens BEFORE <project_vertex> so `transformed` is still in
// geometry-local space; the instanceMatrix then scales + places the inflated
// hull correctly in the world. world-width ≈ uOW × pool.baseScale.
//
// Alpha-cutout submeshes (foliage leaf cards) are skipped: an inflated hull
// on planar two-sided cards produces a duplicate card layer, not a silhouette.
function makeInstancedOutlineMat(localWidth: number): THREE.MeshLambertMaterial {
  const mat = new THREE.MeshLambertMaterial({ color: 0x000000, side: THREE.BackSide, fog: true });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uOW = { value: localWidth };
    shader.vertexShader = "uniform float uOW;\n" +
      shader.vertexShader.replace(
        "#include <project_vertex>",
        `transformed += objectNormal * uOW;\n#include <project_vertex>`,
      );
  };
  return mat;
}

// ── CREATURE DROP BEACONS (Task B, primary visual) ───────────────────────
// A light disc + slim vertical beam for each active creature-drop node so
// loot is findable at MOBA zoom. Much subtler than the death-corpse beacon
// (Aviv: "VERY LIGHT. The corpse beacon is way too much."). Shared geometry +
// materials across all drop beacons; each beacon is a THREE.Group child of
// `beaconGroup` (itself a child of `group`) positioned at the drop location.
const DROP_BEACON_COLOR  = 0xffd24a;  // warm gold (same family as death beacon)
const DROP_DISC_RADIUS   = 0.35;      // subtle ground ring   (death corpse = 0.95)
const DROP_BEAM_HEIGHT   = 1.2;       // short shaft          (death corpse = 3.0)
const DROP_BEAM_RADIUS   = 0.05;      // thin                 (death corpse = 0.12)
const DROP_DISC_OPACITY  = 0.18;      // very light           (death corpse = 0.40)
const DROP_BEAM_OPACITY  = 0.07;      // barely-there         (death corpse = 0.16)

// One sub-pool per source-GLTF variant. Each holds one InstancedMesh per glTF
// primitive (bark + leaves on trees, etc.); all primitives share the same slot
// index space. Slot allocation is monotonic — `remove` zeros the matrix but
// does not free the slot for reuse, matching the existing single-variant
// behavior. `maxCount` is duplicated per variant (uneven hash distribution
// could push >maxCount/N instances into one variant), so total memory grows
// linearly with variant count.
type VariantPool = {
  meshes: THREE.InstancedMesh[];
  outlineMeshes: THREE.InstancedMesh[]; // parallel to meshes, skips cutout submeshes
  nextSlot: number;
};

type Pool = {
  variants: VariantPool[];        // length 1 for single-source kinds
  baseScale: number;
  slots: Map<string, { v: number; s: number }>;  // id → variant index + slot index
};

// Apply a tint to an already-built toon material IN PLACE. We must NOT clone the
// material here: THREE's Material.copy() does not carry `onBeforeCompile`, so a
// clone silently drops the toon rim / world-noise / FOLIAGE WIND shader hooks —
// that was the bug where harvestable trees didn't sway in the wind while the
// (un-cloned) decorative doodad trees did. Each variant builds its own toonMat
// (toToon returns a fresh material), so mutating in place is safe.
function applyTintInPlace(mat: THREE.MeshToonMaterial, tint: Tint): void {
  if (mat.color instanceof THREE.Color) mat.color.set(tint.color);
  if (tint.emissive !== undefined && mat.emissive instanceof THREE.Color) {
    mat.emissive.set(tint.emissive);
    if (tint.emissiveIntensity !== undefined) mat.emissiveIntensity = tint.emissiveIntensity;
  }
}

// Desaturate a (warm) rock atlas to a cool grey so harvestable stone deposits read
// as GREY stone, clearly distinct from the warm-brown decorative rock doodads. A
// shader hue-recolor doesn't read against the lit/graded frame; a genuinely
// greyscale TEXTURE does. `coolHex` is a light cool-grey multiplier applied to the
// per-texel luminance (+ a brightness lift).
function greyifyTexture(src: THREE.Texture, coolHex: number): THREE.Texture {
  const img = src.image as (CanvasImageSource & { width: number; height: number }) | undefined;
  if (!img || !img.width) return src;
  const canvas = document.createElement("canvas");
  canvas.width = img.width; canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img as CanvasImageSource, 0, 0);
  const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const a = d.data;
  const cr = ((coolHex >> 16) & 255) / 255, cg = ((coolHex >> 8) & 255) / 255, cb = (coolHex & 255) / 255;
  const LIFT = 1.12;
  for (let i = 0; i < a.length; i += 4) {
    const lum = 0.299 * a[i]! + 0.587 * a[i + 1]! + 0.114 * a[i + 2]!;
    a[i]     = Math.min(255, lum * cr * LIFT);
    a[i + 1] = Math.min(255, lum * cg * LIFT);
    a[i + 2] = Math.min(255, lum * cb * LIFT);
  }
  ctx.putImageData(d, 0, 0);
  const t = new THREE.CanvasTexture(canvas);
  t.colorSpace = THREE.SRGBColorSpace;
  t.flipY = src.flipY; t.wrapS = src.wrapS; t.wrapT = src.wrapT;
  t.needsUpdate = true;
  return t;
}

// World-spanning sphere used as InstancedMesh.boundingSphere. THREE's
// InstancedMesh.raycast() does an early-reject test against this sphere
// transformed by matrixWorld; if it falls back to the geometry's sphere
// (origin-centered, radius ~3), every ray that doesn't pass within 3u of
// origin misses every resource. Same family of bug as frustum culling. The
// map is 256u square; one centered sphere covers all instances regardless
// of which slot is populated.
const MAP_BOUNDS_SPHERE = new THREE.Sphere(
  new THREE.Vector3(128, 10, 128),
  220,
);

// Resource trees use the shared toon material (`./toon.ts`) — same path as
// world.ts doodads. Cel-shaded 3-band gradient produces the chunky lighting
// step the spec calls for, replacing the prior Lambert+flatShading approach.

// Resource kinds that read as "trees" should cast shadows; bushes and small ground
// pickups stay off (they shimmer at this map size, same rule as `world.ts` doodads).
const TREE_KINDS = new Set<ResourceKind>(["MapleTree", "ElderTree"]);

function buildVariantPool(
  maxCount: number,
  gltf: GLTF,
  tint: Tint | undefined,
  recolor: number | undefined,
  castShadow: boolean,
  outlineWidth: number | undefined,
): VariantPool {
  const meshes: THREE.InstancedMesh[] = [];
  const outlineMeshes: THREE.InstancedMesh[] = [];
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const srcMat = Array.isArray(m.material) ? m.material[0]! : m.material;
    bakeBaseAO(m.geometry);
    // `recolor` (stone deposits) → desaturate the warm rock atlas to a cool grey
    // TEXTURE so it reads as grey stone vs the warm decorative rock doodads. Clone
    // the source standard material first so we don't mutate the shared GLTF map.
    let baseMat = srcMat;
    const srcStd = srcMat as THREE.MeshStandardMaterial;
    if (recolor !== undefined && srcStd.map) {
      baseMat = srcMat.clone();
      (baseMat as THREE.MeshStandardMaterial).map = greyifyTexture(srcStd.map, recolor);
    }
    // `fogMask: true` — resource nodes dissolve away in never-explored fog and
    // stipple-dissolve back in once their ground has been scouted (map memory).
    const toonMat = toToon(baseMat, { noiseScale: 2.5, fogMask: true });
    // `tint` (green maples / gold deposits): apply IN PLACE to keep the wind/rim/
    // noise shader hooks (cloning drops onBeforeCompile — see applyTintInPlace).
    if (tint !== undefined) applyTintInPlace(toonMat, tint);
    const finalMat = toonMat;
    const inst = new THREE.InstancedMesh(m.geometry, finalMat, maxCount);
    inst.castShadow = castShadow;
    // Disable per-mesh frustum culling: InstancedMesh culls against geometry's
    // bounding sphere (origin-centered), which sits 100-200u away from actual
    // resource positions and gets culled when zoomed close. Cost is trivial at
    // our scale (worst case ~5 variants × 287 slots ≈ 1500 instances per kind).
    inst.frustumCulled = false;
    // Override the raycast bounding sphere for the same reason: the rendering
    // path uses geometry sphere for frustum culling, raycasting uses
    // InstancedMesh.boundingSphere (when set) for early reject. Without this,
    // ray-pick of any resource fails unless the ray happens to pass near origin.
    inst.boundingSphere = MAP_BOUNDS_SPHERE.clone();
    // Hide all slots until populated; setMatrixAt(i, ZERO_MATRIX) collapses geometry.
    for (let i = 0; i < maxCount; i++) inst.setMatrixAt(i, ZERO_MATRIX);
    inst.instanceMatrix.needsUpdate = true;
    meshes.push(inst);

    // Companion outline InstancedMesh for solid (non-alpha-cutout) submeshes.
    // Alpha-cutout foliage cards are skipped: inflating their BackSide hull just
    // produces a duplicate leaf layer, not a clean silhouette ring.
    if (outlineWidth !== undefined && finalMat.alphaTest === 0) {
      const oInst = new THREE.InstancedMesh(m.geometry, makeInstancedOutlineMat(outlineWidth), maxCount);
      oInst.castShadow = false;
      oInst.frustumCulled = false;
      oInst.boundingSphere = MAP_BOUNDS_SPHERE.clone();
      for (let i = 0; i < maxCount; i++) oInst.setMatrixAt(i, ZERO_MATRIX);
      oInst.instanceMatrix.needsUpdate = true;
      outlineMeshes.push(oInst);
    }
  });
  if (meshes.length === 0) throw new Error(`pool source glTF has no Mesh node`);
  return { meshes, outlineMeshes, nextSlot: 0 };
}

function buildPool(def: KindDef, assets: AssetBundle, castShadow: boolean, outlineWidth: number | undefined): Pool {
  const sources = def.sources ?? (def.source ? [def.source] : []);
  if (sources.length === 0) throw new Error("KindDef must define source or sources");
  const variants = sources.map((src) => buildVariantPool(def.maxCount, src(assets), def.tint, def.recolor, castShadow, outlineWidth));
  return { variants, baseScale: def.baseScale, slots: new Map() };
}

export type ResourceRenderer = {
  add: (id: string, node: ResourceNode) => void;
  remove: (id: string) => void;
  pickResource: (raycaster: THREE.Raycaster) => { id: string; kind: ResourceKind; point: THREE.Vector3 } | null;
  group: THREE.Group;
};

export function createResourceRenderer(
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
): ResourceRenderer {
  const group = new THREE.Group();
  group.name = "resources";

  // ── Drop beacon shared geometry + materials ──────────────────────────────
  // One disc + one slim beam geometry, shared across all drop beacon groups.
  // Additive blending, no depth write → stack cleanly when multiple drops sit
  // close together (each group adds light, not an opaque occlusion rectangle).
  const dropDiscGeom = new THREE.CircleGeometry(DROP_DISC_RADIUS, 24);
  dropDiscGeom.rotateX(-Math.PI / 2);
  const dropDiscMat = new THREE.MeshBasicMaterial({
    color: DROP_BEACON_COLOR, transparent: true, opacity: DROP_DISC_OPACITY,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const dropBeamGeom = new THREE.CylinderGeometry(DROP_BEAM_RADIUS, DROP_BEAM_RADIUS, DROP_BEAM_HEIGHT, 6, 1, true);
  dropBeamGeom.translate(0, DROP_BEAM_HEIGHT / 2, 0);
  const dropBeamMat = new THREE.MeshBasicMaterial({
    color: DROP_BEACON_COLOR, transparent: true, opacity: DROP_BEAM_OPACITY,
    depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
  // One Group per active drop (positioned at the drop's world location).
  const dropBeacons = new Map<string, THREE.Group>();
  const beaconGroup = new THREE.Group();
  beaconGroup.name = "drop-beacons";
  group.add(beaconGroup);

  const pools = {} as Record<ResourceKind, Pool>;
  for (const k of Object.keys(KINDS) as ResourceKind[]) {
    const def = KINDS[k];
    const outlineWidth = HARVESTABLE_NODE_KINDS.has(k)
      ? NODE_OUTLINE_WIDTH
      : DROP_KINDS_SET.has(k) ? DROP_OUTLINE_WIDTH : undefined;
    pools[k] = buildPool(def, assets, TREE_KINDS.has(k), outlineWidth);
    pools[k].variants.forEach((vp, vi) => {
      for (const m of vp.meshes) {
        m.name = `resources:${k}:v${vi}`;
        group.add(m);
      }
      // Outline InstancedMeshes are added right after their primary mesh so
      // they draw in the same depth pass (BackSide, fog-faded, no shadow).
      for (const m of vp.outlineMeshes) {
        m.name = `resources:${k}:v${vi}:outline`;
        group.add(m);
      }
    });
  }

  const dummy = new THREE.Object3D();

  const add = (id: string, node: ResourceNode): void => {
    const pool = pools[node.kind as ResourceKind];
    if (!pool) {
      console.warn(`[resources] unknown kind: ${node.kind}`);
      return;
    }
    if (pool.slots.has(id)) return; // duplicate add — ignore

    // Pick variant deterministically from node id. Same node always gets the
    // same mesh across reloads / clients.
    const variantIdx = pool.variants.length === 1 ? 0 : hashId(id) % pool.variants.length;
    const vp = pool.variants[variantIdx]!;

    const slot = vp.nextSlot++;
    if (slot >= vp.meshes[0]!.count) {
      console.warn(`[resources] pool overflow for ${node.kind}/v${variantIdx}: slot ${slot}`);
      return;
    }
    pool.slots.set(id, { v: variantIdx, s: slot });

    const y = sampleHeight(node.x, node.z);
    dummy.position.set(node.x, y, node.z);
    // Rotation stays deterministic per-node for stability across reloads.
    const rot = ((hashId(id) >>> 8) % 360) * Math.PI / 180;
    dummy.rotation.set(0, rot, 0);
    dummy.scale.setScalar(pool.baseScale);
    dummy.updateMatrix();
    for (const m of vp.meshes) {
      m.setMatrixAt(slot, dummy.matrix);
      m.instanceMatrix.needsUpdate = true;
    }
    // Outline InstancedMeshes share the same slot space — set the same matrix.
    for (const m of vp.outlineMeshes) {
      m.setMatrixAt(slot, dummy.matrix);
      m.instanceMatrix.needsUpdate = true;
    }

    // Creature drop beacon: a small disc + slim beam so loot is findable.
    if (DROP_KINDS_SET.has(node.kind as ResourceKind)) {
      const bg = new THREE.Group();
      bg.position.set(node.x, y + 0.02, node.z);
      bg.add(new THREE.Mesh(dropDiscGeom, dropDiscMat));
      bg.add(new THREE.Mesh(dropBeamGeom, dropBeamMat));
      beaconGroup.add(bg);
      dropBeacons.set(id, bg);
    }
  };

  const remove = (id: string): void => {
    // Remove drop beacon (if any) regardless of which pool the id lives in.
    const beacon = dropBeacons.get(id);
    if (beacon) {
      beaconGroup.remove(beacon);
      dropBeacons.delete(id);
      // Shared disc/beam geometry + materials — do NOT dispose here.
    }

    for (const k of Object.keys(pools) as ResourceKind[]) {
      const pool = pools[k];
      const ref = pool.slots.get(id);
      if (ref === undefined) continue;
      pool.slots.delete(id);
      const vp = pool.variants[ref.v]!;
      for (const m of vp.meshes) {
        m.setMatrixAt(ref.s, ZERO_MATRIX);
        m.instanceMatrix.needsUpdate = true;
      }
      // Zero the outline InstancedMeshes for the same slot.
      for (const m of vp.outlineMeshes) {
        m.setMatrixAt(ref.s, ZERO_MATRIX);
        m.instanceMatrix.needsUpdate = true;
      }
      return;
    }
  };

  // Raycast against every InstancedMesh across every kind + variant. The first hit
  // (closest) wins. instanceId resolves back to the node id via the per-pool slot map.
  // Three.js InstancedMesh raycasting walks instance bounding boxes natively, so we
  // don't need bvh / instanced-mesh helpers at this scale.
  const allMeshes: THREE.InstancedMesh[] = [];
  for (const k of Object.keys(pools) as ResourceKind[]) {
    for (const vp of pools[k].variants) {
      for (const m of vp.meshes) allMeshes.push(m);
    }
  }

  const pickResource = (
    raycaster: THREE.Raycaster,
  ): { id: string; kind: ResourceKind; point: THREE.Vector3 } | null => {
    const hits = raycaster.intersectObjects(allMeshes, false);
    if (hits.length === 0) return null;
    const hit = hits[0]!;
    const slot = hit.instanceId;
    if (slot === undefined) return null;
    const inst = hit.object as THREE.InstancedMesh;
    for (const k of Object.keys(pools) as ResourceKind[]) {
      const pool = pools[k];
      let variantIdx = -1;
      for (let vi = 0; vi < pool.variants.length; vi++) {
        if (pool.variants[vi]!.meshes.includes(inst)) { variantIdx = vi; break; }
      }
      if (variantIdx === -1) continue;
      for (const [id, ref] of pool.slots) {
        // `point` = world-space hit; the caller height-gates harvest vs. move
        // (clicking high on a tall trunk/canopy should navigate, not harvest).
        if (ref.v === variantIdx && ref.s === slot) return { id, kind: k, point: hit.point };
      }
      return null;
    }
    return null;
  };

  return { add, remove, pickResource, group };
}
